import React, { useState, useEffect, useCallback } from 'react';
import toast from '../../utils/toast';
import {
  Bell,
  X,
  RefreshCw,
  MessageSquare,
  Mail,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  User,
  Filter,
  Flag,
  Eye,
} from 'lucide-react';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import DatePickerField from '../../components/UI/DatePickerField';
import api from '../../services/api';
import { formatDateTime, formatDateForInput } from '../../utils/dateUtils';
import { Pagination } from '../../components/UI/Pagination';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { FilterButton } from '../../components/UI/FilterButton';
import PillSelect from '../../components/UI/PillSelect';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'leave', label: 'Leave & Time-Off' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'documents', label: 'Documents & Certificates' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'compliance', label: 'Compliance Alerts' },
  { value: 'employment', label: 'Employment Status' },
  { value: 'assets', label: 'Assets' },
  { value: 'system', label: 'System Announcements' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const READ_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'false', label: 'Unread' },
  { value: 'true', label: 'Read' },
];

const CATEGORY_STYLES = {
  leave:      { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Leave' },
  attendance: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Attendance' },
  documents:  { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Documents' },
  payroll:    { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Payroll' },
  compliance: { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Compliance' },
  employment: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Employment' },
  assets:     { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Assets' },
  system:     { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'System' },
};

const PRIORITY_STYLES = {
  low:    { bg: 'bg-gray-100',   text: 'text-gray-600'  },
  medium: { bg: 'bg-blue-100',  text: 'text-blue-700'  },
  high:   { bg: 'bg-orange-100', text: 'text-orange-700' },
  urgent: { bg: 'bg-red-100',   text: 'text-red-700'   },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CategoryPill = ({ category }) => {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.system;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
};

const PriorityPill = ({ priority }) => {
  const style = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${style.bg} ${style.text}`}>
      {priority}
    </span>
  );
};

const DeliveryIcons = ({ whatsappSent, emailSent }) => (
  <div className="flex items-center gap-1.5">
    <MessageSquare
      className={`w-4 h-4 ${whatsappSent ? 'text-green-500' : 'text-gray-300'}`}
      title={whatsappSent ? 'WhatsApp sent' : 'WhatsApp not sent'}
    />
    <Mail
      className={`w-4 h-4 ${emailSent ? 'text-blue-500' : 'text-gray-300'}`}
      title={emailSent ? 'Email sent' : 'Email not sent'}
    />
  </div>
);

// ─── Expanded row: message preview ───────────────────────────────────────────

const ExpandedRow = ({ notification }) => (
  <tr className="bg-red-50/40">
    <td colSpan={8} className="px-6 py-3">
      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line border-l-2 border-red-300 pl-3">
        {notification.message}
      </div>
      {(notification.related_object_type || notification.action_url) && (
        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
          {notification.related_object_type && (
            <span>
              Linked to: <span className="font-medium">{notification.related_object_type}</span>
              {notification.related_object_id && ` #${notification.related_object_id}`}
            </span>
          )}
          {notification.action_url && (
            <span>URL: <span className="font-mono">{notification.action_url}</span></span>
          )}
        </div>
      )}
    </td>
  </tr>
);

// ─── Mobile card (below md) ──────────────────────────────────────────────────
// The 8-column desktop table is unreadable on a phone (you'd have to scroll
// sideways to reach the title / status / timestamp). On mobile each
// notification becomes a self-contained card led by the title — the thing an
// admin actually scans for — with recipient, category/priority/read chips,
// delivery icons and the sent-at time. Tapping expands the full message,
// mirroring the desktop row's expand behaviour.
const NotificationCard = ({ notification: n, isExpanded, onToggle }) => (
  <div
    onClick={onToggle}
    className={`bg-white border border-gray-200 rounded-xl p-4 cursor-pointer ${
      !n.is_read ? 'border-l-4 border-l-red-400' : ''
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <p
        className={`text-sm text-gray-900 min-w-0 flex-1 ${
          !n.is_read ? 'font-semibold' : 'font-medium'
        } ${isExpanded ? '' : 'truncate'}`}
        title={n.title}
      >
        {n.title}
      </p>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <DeliveryIcons whatsappSent={n.whatsapp_sent} emailSent={n.email_sent} />
        {isExpanded
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>
    </div>

    {/* Recipient */}
    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500 min-w-0">
      <User className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{n.recipient_name}</span>
      {n.recipient_staff_id && (
        <span className="text-gray-400 flex-shrink-0">· {n.recipient_staff_id}</span>
      )}
    </div>

    {/* Status chips */}
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <CategoryPill category={n.category} />
      <PriorityPill priority={n.priority} />
      {n.is_read ? (
        <span className="inline-flex items-center gap-1 text-xs text-green-600">
          <CheckCircle className="w-3.5 h-3.5" /> Read
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
          <Clock className="w-3.5 h-3.5" /> Unread
        </span>
      )}
    </div>

    {/* Sent at */}
    <p className="text-xs text-gray-400 mt-2">{formatDateTime(n.created_at)}</p>

    {/* Expanded message */}
    {isExpanded && (
      <div className="mt-3 pt-3 border-t border-gray-100" onClick={e => e.stopPropagation()}>
        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line border-l-2 border-red-300 pl-3">
          {n.message}
        </div>
        {(n.related_object_type || n.action_url) && (
          <div className="mt-2 space-y-1 text-xs text-gray-500">
            {n.related_object_type && (
              <div>
                Linked to: <span className="font-medium">{n.related_object_type}</span>
                {n.related_object_id && ` #${n.related_object_id}`}
              </div>
            )}
            {n.action_url && (
              <div className="break-all">URL: <span className="font-mono">{n.action_url}</span></div>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const NotificationLog = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    priority: '',
    is_read: '',
    dateFrom: null,
    dateTo: null,
  });

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ordering: '-created_at',
      };
      if (search)           params.search = search;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      if (filters.is_read !== '') params.is_read = filters.is_read;
      if (filters.dateFrom) params.created_at__gte = formatDateForInput(filters.dateFrom);
      if (filters.dateTo)   params.created_at__lte = formatDateForInput(filters.dateTo);

      const res = await api.get('/admin/staff_core/notification/', { params });
      setNotifications(res.data.results);
      setTotal(res.data.count);
    } catch (err) {
      console.error('[NotificationLog] Failed to load:', err);
      toast.error('Failed to load notification log');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filters]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({ category: '', priority: '', is_read: '', dateFrom: null, dateTo: null });
  };

  const hasActiveFilters =
    search ||
    filters.category ||
    filters.priority ||
    filters.is_read !== '' ||
    filters.dateFrom ||
    filters.dateTo;

  const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));

  const totalPages = Math.ceil(total / pageSize);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-6">

      {/* ── Header ── */}
      {/* Single row at every breakpoint: a lone icon button never needs its
          own line, so it stays inline top-right beside the title. The title
          block truncates (min-w-0) and the button holds its size. */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Notification Log</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Full cross-employee notification history</p>
        </div>
        {/* Refresh is a utility action, not a primary CTA — compact icon button,
            kept inline with the title (flex-shrink-0 so it never wraps). */}
        <button
          onClick={loadNotifications}
          disabled={loading}
          className="icon-button-admin flex-shrink-0"
          title={loading ? 'Loading…' : 'Refresh'}
          aria-label={loading ? 'Loading' : 'Refresh'}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Search + Filter toolbar ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        {/* Search takes the full width on mobile; the controls (rows-per-page,
            filter, clear) drop to their own row instead of being crushed onto
            one line. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Search */}
          <DebouncedSearchInput
            value={search}
            onSearch={setSearch}
            placeholder="Search by recipient, title, or message…"
            className="w-full sm:max-w-xl sm:flex-1"
          />

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Rows per page — inline with toolbar */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="text-xs whitespace-nowrap">Rows per page:</span>
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="select-admin text-xs py-1 px-2"
              >
                {[10, 20, 50, 100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Filter toggle */}
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={[filters.category, filters.priority, filters.is_read, filters.dateFrom, filters.dateTo].filter(Boolean).length}
              onClick={() => setShowFilters(v => !v)}
              className="flex-shrink-0"
            />

            {/* Clear */}
            {hasActiveFilters && (
              <button onClick={clearFilters} className="icon-button-admin flex-shrink-0" title="Clear all filters">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Expanded filter row */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <PillSelect
                options={CATEGORY_OPTIONS}
                value={filters.category}
                onChange={(val) => handleFilterChange('category', val)}
                theme="admin"
                icon={Filter}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <PillSelect
                options={PRIORITY_OPTIONS}
                value={filters.priority}
                onChange={(val) => handleFilterChange('priority', val)}
                theme="admin"
                icon={Flag}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>

            {/* Read status */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Read Status</label>
              <PillSelect
                options={READ_OPTIONS}
                value={filters.is_read}
                onChange={(val) => handleFilterChange('is_read', val)}
                theme="admin"
                icon={Eye}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
              <DatePickerField
                selected={filters.dateFrom}
                onChange={d => handleFilterChange('dateFrom', d)}
                placeholderText="From date"
                className="input-admin pr-3 text-sm w-full"
                isClearable
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
              <DatePickerField
                selected={filters.dateTo}
                onChange={d => handleFilterChange('dateTo', d)}
                placeholderText="To date"
                className="input-admin pr-3 text-sm w-full"
                isClearable
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Result count ── */}
      <p className="text-sm text-gray-500">
        {loading ? 'Loading…' : `${total.toLocaleString()} notification${total !== 1 ? 's' : ''}`}
      </p>

      {/* ── List: shared loading / empty states, then cards (mobile) or table (desktop) ── */}
      {loading && notifications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl px-6 py-12 text-center">
          <RefreshCw className="w-6 h-6 text-gray-300 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-400">Loading notifications…</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl px-6 py-12 text-center">
          <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No notifications found</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-2 text-xs text-red-600 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {notifications.map(n => (
              <NotificationCard
                key={n.id}
                notification={n}
                isExpanded={expandedRow === n.id}
                onToggle={() => toggleRow(n.id)}
              />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell w-8" />
                    <th className="table-header-cell">
                      <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Recipient</div>
                    </th>
                    <th className="table-header-cell">Category</th>
                    <th className="table-header-cell">Title</th>
                    <th className="table-header-cell text-center">Priority</th>
                    <th className="table-header-cell text-center">Read</th>
                    <th className="table-header-cell text-center">
                      <div className="flex items-center justify-center gap-1.5" title="WhatsApp / Email">Delivery</div>
                    </th>
                    <th className="table-header-cell text-right">Sent At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {notifications.map(n => {
                    const isExpanded = expandedRow === n.id;
                    return (
                      <React.Fragment key={n.id}>
                        <tr
                          onClick={() => toggleRow(n.id)}
                          className={`cursor-pointer table-row ${
                            isExpanded ? 'bg-red-50/30' : ''
                          } ${!n.is_read ? 'font-medium' : ''}`}
                        >
                          {/* Expand chevron */}
                          <td className="px-4 py-3 text-gray-400">
                            {isExpanded
                              ? <ChevronUp className="w-4 h-4" />
                              : <ChevronDown className="w-4 h-4" />}
                          </td>

                          {/* Recipient */}
                          <td className="px-6 py-3">
                            <div className="text-sm text-gray-900">{n.recipient_name}</div>
                            <div className="text-xs text-gray-400">{n.recipient_staff_id}</div>
                          </td>

                          {/* Category */}
                          <td className="px-6 py-3">
                            <CategoryPill category={n.category} />
                            <div className="text-xs text-gray-400 mt-0.5">{n.type_display}</div>
                          </td>

                          {/* Title */}
                          <td className="px-6 py-3 max-w-xs">
                            <p className="text-sm text-gray-900 truncate" title={n.title}>{n.title}</p>
                          </td>

                          {/* Priority */}
                          <td className="px-6 py-3 text-center">
                            <PriorityPill priority={n.priority} />
                          </td>

                          {/* Read */}
                          <td className="px-6 py-3 text-center">
                            {n.is_read ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                <CheckCircle className="w-3.5 h-3.5" /> Read
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                <Clock className="w-3.5 h-3.5" /> Unread
                              </span>
                            )}
                          </td>

                          {/* Delivery */}
                          <td className="px-6 py-3 text-center">
                            <DeliveryIcons
                              whatsappSent={n.whatsapp_sent}
                              emailSent={n.email_sent}
                            />
                          </td>

                          {/* Sent at */}
                          <td className="px-6 py-3 text-xs text-gray-500 whitespace-nowrap text-right">
                            {formatDateTime(n.created_at)}
                          </td>
                        </tr>

                        {isExpanded && <ExpandedRow notification={n} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Pagination ── */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={total}
        pageSize={pageSize}
        theme="admin"
        noun="notifications"
      />
    </div>
  );
};

export default NotificationLog;
